/**
 * HelpButton — JIT learning trigger for feature pages.
 *
 * Audit A16 (Nielsen Heuristic 10): produto método-driven (Rafa Medeiros)
 * sem explainers contextuais. Usuário toca em uma feature sem entender
 * QUE etapa do método ela cobre, COMO usar, ou POR QUE existe. Esta UI
 * resolve via "?" icon ao lado do título → modal curto explicando
 * propósito + como usar + dica do método.
 *
 * Princípio: just-in-time learning, não wall-of-docs. Conteúdo curto,
 * acionável, vocabulário do método.
 */
import { Question } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface HelpButtonProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  ariaLabel?: string;
}

export function HelpButton({
  title,
  description,
  children,
  ariaLabel,
}: HelpButtonProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel ?? `Ajuda: ${title}`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Question className="h-5 w-5" weight="duotone" aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3 text-sm text-foreground/90 leading-relaxed">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
